import { AxisKind, ClassifiedObjectType, ValueType } from '@atlas/types'
import { Button } from '@atlas/ui-primitives'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UploadTile } from '@/components/UploadTile'
import { useFileUpload } from '@/hooks/useFileUpload'
import { notification } from '@/notifications/notification-store'
import { isCancellableStatus, isConfirmedStatus, UploadItemStatus } from '@/upload/upload-store'
import { BatchClassificationPanel } from './BatchClassificationPanel'
import { ClassifyGrid } from './ClassifyGrid'
import { CommitFooter } from './CommitFooter'
import { REQUIRED_DIMENSIONS } from './classify-config'
import type { ClassifyRowView } from './row-view'
import { StagingDropzone } from './StagingDropzone'
import type { PanelAxis } from './types'
import { useClassifyStaging } from './useClassifyStaging'
import { UPLOAD_CLASSIFY_OWNER, useCommitUploads } from './useCommitUploads'
import { usePanelAxes } from './usePanelAxes'
import { type StagedFile, useStagedFiles } from './useStagedFiles'

/**
 * Upload & classify: drop files, classify them, then commit — which is when
 * they upload.
 *
 * The order matters. Dropping a file stages it locally and sends nothing; the
 * tuples are submitted with each file's own `confirmUpload`, so an asset is
 * classified from the moment it exists server-side. That is also what lets
 * post-upload concerns depend on `classification` — a dependency that cannot
 * hold if the file is uploaded first and tagged afterwards.
 */
/** Page title + the way back to the library. */
function PageHeader({ onBack }: { onBack: () => void }) {
	return (
		<div className="flex items-start justify-between">
			<div>
				<p className="text-ds-xs font-medium uppercase tracking-widest text-ds-text-subtle">
					Library · Asset upload &amp; classify
				</p>
				<h1 className="font-ds-display text-ds-2xl text-ds-text">Upload &amp; classify</h1>
			</div>
			<Button variant="secondary" onPress={onBack}>
				Back to library
			</Button>
		</div>
	)
}

/**
 * One file's chosen axis/value pairs as classification tuples.
 *
 * Empty values are dropped rather than sent: an axis the user left blank is
 * "not classified on this axis", which is not the same as classified to the
 * empty string — the latter would create a meaningless assertion. Attribute
 * axes are excluded — those commit through `setAttribute`, not `classifyBatch`
 * (see {@link toAttributeEntries}).
 *
 * Callers must not reach here with an empty `axes`: the split is driven by the
 * registry, so an unresolved registry cannot be distinguished from "every axis
 * is an attribute", and the filter would silently drop every tuple and upload
 * the file unclassified. {@link UploadClassifyPage} blocks the commit instead.
 */
export function toTuples(classification: Record<string, string>, fileId: string, axes: PanelAxis[]) {
	const dimensionCodes = new Set(axes.filter((a) => a.kind === AxisKind.DIMENSION).map((a) => a.code))
	return Object.entries(classification)
		.filter(([axisCode, valueCode]) => valueCode !== '' && dimensionCodes.has(axisCode))
		.map(([axisCode, valueCode]) => ({
			objectType: ClassifiedObjectType.FILE,
			objectId: fileId,
			axisCode,
			valueCode,
		}))
}

/**
 * One file's resolved attribute values — the sibling of {@link toTuples} for
 * `kind='attribute'` axis codes. An axis with no resolved value is omitted, not
 * sent as an empty string.
 *
 * A `number` axis is normalised through `Number` before it is sent, and dropped
 * if that fails. `setAttribute` stores whatever string it is handed — it does
 * not check the axis's `value_type` — so `" 12"` and `"1e5"` would otherwise
 * persist verbatim and land on every later reader to parse defensively. What is
 * stored is the canonical form (`"12"`, `"100000"`).
 */
export function toAttributeEntries(
	classification: Record<string, string>,
	axes: PanelAxis[],
): { axisCode: string; valueCode: string }[] {
	const attributeAxes = new Map(axes.filter((a) => a.kind === AxisKind.ATTRIBUTE).map((a) => [a.code, a]))
	return Object.entries(classification)
		.filter(([axisCode, valueCode]) => valueCode !== '' && attributeAxes.has(axisCode))
		.map(([axisCode, valueCode]) => {
			if (attributeAxes.get(axisCode)?.valueType !== ValueType.NUMBER) return { axisCode, valueCode }
			const parsed = Number(valueCode)
			return Number.isFinite(parsed) ? { axisCode, valueCode: String(parsed) } : null
		})
		.filter((entry): entry is { axisCode: string; valueCode: string } => entry !== null)
}

interface RunCommitArgs {
	axes: PanelAxis[]
	axesLoading: boolean
	staged: StagedFile[]
	staging: ReturnType<typeof useClassifyStaging>
	commit: ReturnType<typeof useCommitUploads>['commit']
	removeMany: (stagedIds: string[]) => void
	setErrors: (errors: Record<string, string>) => void
	setShowErrors: (show: boolean) => void
}

/**
 * Commit the ready files: upload each with its own tuples, then drop what landed
 * from the staging table and keep what didn't, with its error.
 */
async function runCommit({
	axes,
	axesLoading,
	staged,
	staging,
	commit,
	removeMany,
	setErrors,
	setShowErrors,
}: RunCommitArgs): Promise<void> {
	// Attempting a commit is the first moment an empty required field is
	// genuinely a problem rather than a starting state, so that is when the
	// errors appear — a freshly dropped file should not open covered in red.
	setShowErrors(true)
	// A hard stop, not a degraded commit. `toTuples`/`toAttributeEntries` split
	// on the registry, so with no axes resolved BOTH come back empty and every
	// file would upload silently unclassified while the table reported success.
	// Uploading is the irreversible half, so refuse rather than guess. The footer
	// already disables the button in this state; this is the same rule enforced
	// where the upload actually happens.
	if (axes.length === 0) {
		notification.error(
			axesLoading
				? 'Still loading the classification axes — try again in a moment.'
				: 'The classification axes could not be loaded, so files cannot be committed. Reload and try again.',
		)
		return
	}
	const ready = staged.filter((entry) => staging.readiness(entry.stagedId).ready)
	if (ready.length === 0) return

	const { uploadedIds, errors } = await commit(
		ready.map((entry) => ({
			staged: entry,
			// Deferred: every tuple names the file it classifies, and that id
			// only exists once the upload URL has been issued.
			tuples: (fileId: string) => toTuples(staging.effectiveClassification(entry.stagedId), fileId, axes),
			attributes: () => toAttributeEntries(staging.effectiveClassification(entry.stagedId), axes),
		})),
	)

	// Only what landed leaves the table; the rest keep their error so pressing
	// Commit again retries exactly the files that failed.
	staging.clearRows(uploadedIds)
	removeMany(uploadedIds)
	setErrors(errors)
}

export function UploadClassifyPage() {
	const navigate = useNavigate()
	const { staged, add, remove, removeMany, setErrors } = useStagedFiles()
	const staging = useClassifyStaging()
	// The batch tuple cross-filters the pickers: choosing a geography narrows
	// brand to that market. One value per axis here, so each becomes a
	// single-element array.
	const batchSelection = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(staging.batch)
					.filter(([, value]) => value !== '')
					.map(([code, value]) => [code, [value]]),
			),
		[staging.batch],
	)
	const { axes, isLoading: axesLoading } = usePanelAxes(batchSelection)
	const { commit, isCommitting } = useCommitUploads()
	// Required-field errors stay hidden until the user tries to commit — see
	// `handleCommit`.
	const [showErrors, setShowErrors] = useState(false)
	// Tiles for the batch currently uploading — these enter the global store
	// only at commit, so before that this list is empty.
	const { items, retryConcern, recover, cancelUpload } = useFileUpload(UPLOAD_CLASSIFY_OWNER)

	const rows: ClassifyRowView[] = staged.map((entry) => ({
		stagedId: entry.stagedId,
		fileName: entry.fileName,
		contentType: entry.contentType,
		previewUrl: entry.previewUrl,
		classification: staging.effectiveClassification(entry.stagedId),
		readiness: staging.readiness(entry.stagedId),
		error: entry.error,
	}))
	const readyCount = rows.filter((row) => row.readiness.ready).length

	const handleCommit = () =>
		runCommit({ axes, axesLoading, staged, staging, commit, removeMany, setErrors, setShowErrors })

	return (
		<div className="space-y-6">
			<PageHeader onBack={() => void navigate('/assets')} />

			<StagingDropzone onFilesStaged={add} isDisabled={isCommitting} />

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_1fr]">
				<BatchClassificationPanel
					axes={axes}
					values={staging.batch}
					onChange={staging.setBatchValue}
					requiredAxisCodes={REQUIRED_DIMENSIONS}
					showErrors={showErrors}
				/>
				{/* No panel chrome around the grid: each card carries its own border
				    and surface, so a frame here would double every edge.

				    Nothing stretches to match the rail's height. With no files there
				    is genuinely nothing to put in that space, and both a dashed box
				    and a tall footer are just a void with a border drawn round it. */}
				<div className="space-y-4">
					<ClassifyGrid
						rows={rows}
						axes={axes}
						requiredAxisCodes={REQUIRED_DIMENSIONS}
						showErrors={showErrors}
						onOverride={staging.setRowOverride}
						onRemove={remove}
					/>
					<CommitFooter
						total={rows.length}
						readyCount={readyCount}
						onCommit={() => void handleCommit()}
						isCommitting={isCommitting}
						errors={[...new Set(rows.map((row) => row.error).filter((error): error is string => Boolean(error)))]}
						// Only once loading has settled: mid-fetch, an empty registry is
						// "not yet", not "not available", and flashing an error would be wrong.
						axesUnavailable={!axesLoading && axes.length === 0}
					/>
				</div>
			</div>

			{items.length > 0 && (
				<ul className="flex flex-col gap-ds-2">
					{/*
					 * Post-commit progress. A committed file leaves the classify table
					 * and appears here, where its per-concern state (including the
					 * classification it was committed with) is visible and retryable.
					 */}
					{items.map((item) => (
						<UploadTile
							key={item.id}
							fileName={item.fileName}
							fileSize={item.fileSize}
							contentType={item.contentType}
							file={item.file}
							previewUrl={item.previewUrl}
							uploadProgress={item.progress}
							isConfirming={item.status === UploadItemStatus.CONFIRMING}
							isConfirmed={isConfirmedStatus(item.status)}
							concerns={item.concerns}
							attributeFailures={item.attributeFailures}
							rejectedReason={item.status === UploadItemStatus.REJECTED ? (item.error ?? 'Upload failed') : undefined}
							failureReason={item.status === UploadItemStatus.FAILED ? (item.error ?? 'Upload failed') : undefined}
							recoveryActions={item.recovery?.actions}
							failureIsTerminal={item.recovery?.terminal ?? true}
							onRecover={(action) => recover(item.id, action)}
							onCancel={isCancellableStatus(item.status) ? () => cancelUpload(item.id) : undefined}
							onRetryConcern={(concernId) => void retryConcern(item.id, concernId)}
						/>
					))}
				</ul>
			)}
		</div>
	)
}
